/* Compact branded PDF exporters for VSM / 5S / Kaizen. Landscape A4 unless noted. */
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

const DEFAULT_PRIMARY = [75, 94, 77];
const DEFAULT_ACCENT = [124, 154, 126];
const DARK = [28, 36, 29];
const LIGHT = [197, 214, 199];
const BG = [250, 250, 247];
const WHITE = [255, 255, 255];

function hexToRgb(hex) {
  if (!hex) return null;
  const m = hex.replace("#", "").match(/^([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i);
  return m ? [parseInt(m[1], 16), parseInt(m[2], 16), parseInt(m[3], 16)] : null;
}

async function loadImageDataURL(url) {
  if (!url) return null;
  try {
    const fullUrl = url.startsWith("http") ? url : `${process.env.REACT_APP_BACKEND_URL}${url}`;
    const res = await fetch(fullUrl);
    if (!res.ok) return null;
    const blob = await res.blob();
    return await new Promise(r => { const rd = new FileReader(); rd.onloadend = () => r(rd.result); rd.onerror = () => r(null); rd.readAsDataURL(blob); });
  } catch { return null; }
}

function addHeader(doc, primary, firmName, toolName, org, W) {
  doc.setFillColor(...primary);
  doc.rect(0, 0, W, 10, "F");
  doc.setTextColor(...WHITE);
  doc.setFontSize(9);
  doc.setFont("helvetica", "bold");
  doc.text(`${firmName || "TheKaizenCabinet"}  ·  ${toolName}`, 10, 6.5);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.text(org || "", W / 2, 6.5, { align: "center" });
  doc.setTextColor(...DARK);
}

function addFooter(doc, accent, footerText, hidePowered, W, H, generatedAt) {
  doc.setDrawColor(...LIGHT); doc.setLineWidth(0.3); doc.line(10, H - 8, W - 10, H - 8);
  doc.setFontSize(7); doc.setTextColor(...accent);
  doc.text(hidePowered ? generatedAt : `Generated by TheKaizenCabinet · ${generatedAt}`, 10, H - 4);
  doc.text(footerText || "Confidential — For internal use only", W - 10, H - 4, { align: "right" });
  doc.setTextColor(...DARK);
}

async function brandedDoc(branding, toolName, orgName) {
  const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
  const W = doc.internal.pageSize.width, H = doc.internal.pageSize.height;
  const primary = hexToRgb(branding?.primary_color) || DEFAULT_PRIMARY;
  const accent = hexToRgb(branding?.accent_color) || DEFAULT_ACCENT;
  const firmName = branding?.firm_name || "";
  const footerText = branding?.footer_text || "";
  const hidePowered = !!branding?.hide_powered_by;
  const logoDataUrl = await loadImageDataURL(branding?.logo_url);
  const generatedAt = new Date().toLocaleString();

  const newPage = (first = false) => {
    if (!first) doc.addPage();
    addHeader(doc, primary, firmName, toolName, orgName, W);
  };
  const finishPage = () => addFooter(doc, accent, footerText, hidePowered, W, H, generatedAt);

  return { doc, W, H, primary, accent, firmName, footerText, hidePowered, logoDataUrl, generatedAt, newPage, finishPage };
}

function cover(ctx, title, subtitle) {
  const { doc, W, primary, accent, logoDataUrl, firmName } = ctx;
  doc.setFillColor(...BG); doc.rect(10, 16, W - 20, 28, "F");
  doc.setDrawColor(...LIGHT); doc.rect(10, 16, W - 20, 28);
  if (logoDataUrl) { try { doc.addImage(logoDataUrl, "PNG", 14, 20, 20, 20, undefined, "FAST"); } catch { /* noop */ } }
  doc.setFontSize(20); doc.setFont("helvetica", "bold"); doc.setTextColor(...primary);
  doc.text(title, W / 2, 30, { align: "center" });
  doc.setFontSize(11); doc.setFont("helvetica", "normal"); doc.setTextColor(...DARK);
  doc.text(subtitle || "", W / 2, 38, { align: "center" });
  doc.setFontSize(9); doc.setTextColor(...accent);
  doc.text(firmName ? `Prepared by ${firmName}` : "", W / 2, 42, { align: "center" });
  doc.setTextColor(...DARK);
}

function table(ctx, startY, head, body, columnStyles) {
  const { doc, primary } = ctx;
  autoTable(doc, {
    startY, head: [head], body,
    margin: { left: 10, right: 10 },
    styles: { fontSize: 9, cellPadding: 2.2, textColor: DARK, overflow: "linebreak" },
    headStyles: { fillColor: primary, textColor: WHITE, fontStyle: "bold", fontSize: 9 },
    alternateRowStyles: { fillColor: [248, 250, 248] },
    columnStyles,
  });
  return doc.lastAutoTable.finalY;
}

// ───────────────────────── VSM ─────────────────────────
export async function exportVsmPDF({ project, company, branding }) {
  const d = project?.tool_data || {};
  const orgName = company?.name || project?.setup?.org_profile?.company_name || "Organisation";
  const ctx = await brandedDoc(branding, "Value Stream Mapping", orgName);
  ctx.newPage(true);
  cover(ctx, "Value Stream Map", `${orgName}  ·  ${d.scope?.product_family || "—"}`);
  let y = 50;
  y = table(ctx, y, ["Scope Input", "Value"], [
    ["Product / service family", d.scope?.product_family || "—"],
    ["Start boundary", d.scope?.start || "—"],
    ["End boundary", d.scope?.end || "—"],
    ["Monthly demand", String(d.scope?.volume || 0)],
    ["Takt time (min)", String(d.scope?.takt_minutes || 0)],
  ], { 0: { fontStyle: "bold", cellWidth: 80 }, 1: { cellWidth: 197 } }) + 6;

  const renderSteps = (title, steps) => {
    if (y + 40 > ctx.H - 12) { ctx.finishPage(); ctx.newPage(); y = 16; }
    ctx.doc.setFontSize(11); ctx.doc.setFont("helvetica", "bold"); ctx.doc.setTextColor(...ctx.primary);
    ctx.doc.text(title, 10, y); ctx.doc.setTextColor(...DARK); ctx.doc.setFont("helvetica", "normal"); y += 4;
    y = table(ctx, y, ["Step", "Type", "Cycle (min)", "Changeover", "Uptime %", "Defect %", "Inventory", "Operators"],
      (steps || []).map(s => [s.name, s.type, String(s.cycle_time_min || 0), String(s.changeover_min || 0), String(s.uptime_pct || 0), String(s.defect_pct || 0), String(s.inventory_units || 0), String(s.operators || 0)]),
      { 0: { fontStyle: "bold", cellWidth: 70 } }) + 6;
  };
  renderSteps("Current State", d.current_steps);
  renderSteps("Future State", d.future_steps);

  // Improvement
  if (y + 40 > ctx.H - 12) { ctx.finishPage(); ctx.newPage(); y = 16; }
  ctx.doc.setFontSize(11); ctx.doc.setFont("helvetica", "bold"); ctx.doc.setTextColor(...ctx.primary);
  ctx.doc.text("Improvement Plan", 10, y); ctx.doc.setTextColor(...DARK); ctx.doc.setFont("helvetica", "normal"); y += 4;
  y = table(ctx, y, ["Idea", "Owner", "Due", "Impact"],
    (d.improvement?.ideas || []).map(i => [i.title, i.owner, i.due, i.impact]),
    { 0: { cellWidth: 130 } });

  ctx.finishPage();
  ctx.doc.save(`VSM_${orgName.replace(/\s+/g, "_")}_${new Date().toISOString().slice(0, 10)}.pdf`);
}

// ───────────────────────── 5S ─────────────────────────
const PILLARS = ["Sort", "Set in Order", "Shine", "Standardise", "Sustain"];
const PILLAR_KEYS = ["sort", "set", "shine", "standardise", "sustain"];

export async function exportFiveSPDF({ project, company, branding }) {
  const d = project?.tool_data || {};
  const orgName = company?.name || "Organisation";
  const ctx = await brandedDoc(branding, "5S Workplace Audit", orgName);
  ctx.newPage(true);
  cover(ctx, "5S Audit Report", `${orgName}  ·  ${d.area || ""}`);
  let y = 50;
  const scoreByKey = (k) => {
    const e = d.pillars?.[k];
    const scores = e?.scores || [];
    return scores.reduce((a, s) => a + (Number(s) || 0), 0);
  };
  const perPillarPct = PILLAR_KEYS.map(k => {
    const e = d.pillars?.[k];
    const count = (e?.scores || []).length || 4;
    return { k, sum: scoreByKey(k), max: count * 5, pct: (count * 5) ? (scoreByKey(k) / (count * 5)) * 100 : 0 };
  });
  const totalSum = perPillarPct.reduce((a, p) => a + p.sum, 0);
  const totalMax = perPillarPct.reduce((a, p) => a + p.max, 0);
  const overall = totalMax > 0 ? (totalSum / totalMax) * 100 : 0;

  y = table(ctx, y, ["Audit Meta", "Value"], [
    ["Area", d.area || "—"],
    ["Auditor", d.auditor || "—"],
    ["Date", d.audit_date || "—"],
    ["Target score", `${d.target_pct || 80}%`],
    ["Overall score", `${overall.toFixed(1)}% (${totalSum}/${totalMax})`],
    ["Result", overall >= (d.target_pct || 80) ? "PASS" : "BELOW TARGET"],
  ], { 0: { fontStyle: "bold", cellWidth: 60 } }) + 6;

  y = table(ctx, y, ["Pillar", "Score", "Max", "%", "Status"],
    perPillarPct.map((p, i) => [PILLARS[i], String(p.sum), String(p.max), `${p.pct.toFixed(0)}%`, p.pct >= (d.target_pct || 80) ? "On target" : "Below"]),
    { 0: { fontStyle: "bold", cellWidth: 60 } }) + 6;

  // Notes
  if (y + 40 > ctx.H - 12) { ctx.finishPage(); ctx.newPage(); y = 16; }
  const notesRows = PILLAR_KEYS.map((k, i) => [PILLARS[i], (d.pillars?.[k]?.notes || "—")]).filter(r => r[1]);
  if (notesRows.length) {
    ctx.doc.setFontSize(11); ctx.doc.setFont("helvetica", "bold"); ctx.doc.setTextColor(...ctx.primary);
    ctx.doc.text("Observations & Remediation", 10, y); ctx.doc.setTextColor(...DARK); ctx.doc.setFont("helvetica", "normal"); y += 4;
    y = table(ctx, y, ["Pillar", "Notes"], notesRows, { 0: { fontStyle: "bold", cellWidth: 60 } });
  }
  ctx.finishPage();
  ctx.doc.save(`5S_Audit_${orgName.replace(/\s+/g, "_")}_${new Date().toISOString().slice(0, 10)}.pdf`);
}

// ───────────────────────── Kaizen ─────────────────────────
export async function exportKaizenPDF({ project, company, branding }) {
  const d = project?.tool_data || {};
  const orgName = company?.name || "Organisation";
  const ctx = await brandedDoc(branding, "Kaizen Event Charter", orgName);
  ctx.newPage(true);
  cover(ctx, "Kaizen Charter", `${orgName}  ·  ${d.process_area || ""}`);
  let y = 50;

  y = table(ctx, y, ["Charter Element", "Detail"], [
    ["Problem statement", d.problem || "—"],
    ["Process / area", d.process_area || "—"],
    ["Sponsor", d.sponsor || "—"],
    ["Format", d.format || "—"],
    ["Business impact (USD/yr)", String(d.business_impact || 0)],
    ["In scope", d.in_scope || "—"],
    ["Out of scope", d.out_scope || "—"],
  ], { 0: { fontStyle: "bold", cellWidth: 55 } }) + 6;

  if (d.team?.length) {
    if (y + 30 > ctx.H - 12) { ctx.finishPage(); ctx.newPage(); y = 16; }
    ctx.doc.setFontSize(11); ctx.doc.setFont("helvetica", "bold"); ctx.doc.setTextColor(...ctx.primary);
    ctx.doc.text("Team", 10, y); ctx.doc.setTextColor(...DARK); ctx.doc.setFont("helvetica", "normal"); y += 4;
    y = table(ctx, y, ["Name", "Role / Title", "Type"], d.team.map(m => [m.name, m.role, m.role_type])) + 6;
  }

  if (d.metrics?.length) {
    if (y + 30 > ctx.H - 12) { ctx.finishPage(); ctx.newPage(); y = 16; }
    ctx.doc.setFontSize(11); ctx.doc.setFont("helvetica", "bold"); ctx.doc.setTextColor(...ctx.primary);
    ctx.doc.text("Metrics — Current vs Target", 10, y); ctx.doc.setTextColor(...DARK); ctx.doc.setFont("helvetica", "normal"); y += 4;
    y = table(ctx, y, ["Metric", "Current", "Target", "Unit", "Change"],
      d.metrics.map(m => [m.name, String(m.current || 0), String(m.target || 0), m.unit || "", `${((Number(m.target || 0)) - (Number(m.current || 0))).toFixed(1)}`])) + 6;
  }

  if (d.experiments?.length) {
    if (y + 30 > ctx.H - 12) { ctx.finishPage(); ctx.newPage(); y = 16; }
    ctx.doc.setFontSize(11); ctx.doc.setFont("helvetica", "bold"); ctx.doc.setTextColor(...ctx.primary);
    ctx.doc.text("Experiments", 10, y); ctx.doc.setTextColor(...DARK); ctx.doc.setFont("helvetica", "normal"); y += 4;
    y = table(ctx, y, ["#", "Hypothesis", "Plan", "Predicted", "Actual", "Learning"],
      d.experiments.map((e, i) => [`${i + 1}`, e.hypothesis, e.plan, e.predicted, e.actual, e.learn])) + 6;
  }

  if (d.outcomes || d.sustainment) {
    if (y + 30 > ctx.H - 12) { ctx.finishPage(); ctx.newPage(); y = 16; }
    y = table(ctx, y, ["Outcome / Sustainment", "Detail"], [
      ["Key outcomes", d.outcomes || "—"],
      ["Sustainment plan", d.sustainment || "—"],
      ["Realised savings (USD)", String(d.realised_savings || 0)],
    ], { 0: { fontStyle: "bold", cellWidth: 55 } });
  }

  ctx.finishPage();
  ctx.doc.save(`Kaizen_Charter_${orgName.replace(/\s+/g, "_")}_${new Date().toISOString().slice(0, 10)}.pdf`);
}

// ───────────────────────── SIPOC ─────────────────────────
const SIPOC_COL_KEYS = ["suppliers", "inputs", "process", "outputs", "customers"];
const SIPOC_COL_LABELS = ["Suppliers", "Inputs", "Process", "Outputs", "Customers"];

export async function exportSipocPDF({ project, company, branding }) {
  const d = project?.tool_data || {};
  const orgName = company?.name || "Organisation";
  const ctx = await brandedDoc(branding, "SIPOC Mapper", orgName);
  ctx.newPage(true);
  cover(ctx, "SIPOC", `${orgName} · ${d.process_name || "—"}`);
  let y = 50;
  y = table(ctx, y, ["Scope", "Detail"], [
    ["Process", d.process_name || "—"], ["Owner", d.owner || "—"],
    ["Boundary", d.boundary || "—"], ["Goal", d.goal || "—"],
  ], { 0: { fontStyle: "bold", cellWidth: 60 } }) + 6;
  const maxRows = Math.max(...SIPOC_COL_KEYS.map(k => (d[k] || []).length), 1);
  const body = [];
  for (let r = 0; r < maxRows; r++) {
    body.push(SIPOC_COL_KEYS.map(k => (d[k] && d[k][r] ? `${d[k][r].text}${d[k][r].note ? `\n(${d[k][r].note})` : ""}` : "")));
  }
  table(ctx, y, SIPOC_COL_LABELS, body, {});
  ctx.finishPage();
  ctx.doc.save(`SIPOC_${orgName.replace(/\s+/g, "_")}_${new Date().toISOString().slice(0, 10)}.pdf`);
}

// ───────────────────────── VoC + Kano ─────────────────────────
export async function exportVocPDF({ project, company, branding }) {
  const d = project?.tool_data || {};
  const orgName = company?.name || "Organisation";
  const ctx = await brandedDoc(branding, "Voice of Customer + Kano", orgName);
  ctx.newPage(true);
  cover(ctx, "VoC + Kano", `${orgName} · ${d.product || "—"}`);
  let y = 50;
  y = table(ctx, y, ["Setup", "Detail"], [
    ["Product / service", d.product || "—"], ["Customer segment", d.segment || "—"],
    ["Sample size (N)", String(d.sample_size || 0)], ["Channels", d.channels || "—"],
  ], { 0: { fontStyle: "bold", cellWidth: 55 } }) + 6;
  const items = (d.ctqs || []).map(it => [
    it.voc || "—", it.ctq || "—", it.category || "—",
    String(it.importance || 0), String(it.satisfaction_now || 0), String(it.target || 0),
    String((it.importance || 0) * Math.max(0, (it.target || 0) - (it.satisfaction_now || 0))),
  ]);
  table(ctx, y, ["Voice (verbatim)", "CTQ", "Kano", "Imp", "Now", "Target", "Gap"], items,
    { 0: { cellWidth: 80 }, 1: { cellWidth: 80 } });
  ctx.finishPage();
  ctx.doc.save(`VoC_Kano_${orgName.replace(/\s+/g, "_")}_${new Date().toISOString().slice(0, 10)}.pdf`);
}

// ───────────────────────── Time Study ─────────────────────────
function tsStats(values) {
  if (!values?.length) return { mean: 0, range: 0, cv: 0 };
  const mean = values.reduce((a, v) => a + v, 0) / values.length;
  const min = Math.min(...values), max = Math.max(...values);
  const variance = values.reduce((a, v) => a + (v - mean) ** 2, 0) / values.length;
  const sd = Math.sqrt(variance);
  return { mean, range: max - min, cv: mean ? (sd / mean) * 100 : 0 };
}
export async function exportTimeStudyPDF({ project, company, branding }) {
  const d = project?.tool_data || {};
  const orgName = company?.name || "Organisation";
  const ctx = await brandedDoc(branding, "Process Observation / Time Study", orgName);
  ctx.newPage(true);
  cover(ctx, "Time Study", `${orgName} · ${d.process || "—"}`);
  let y = 50;
  y = table(ctx, y, ["Setup", "Value"], [
    ["Process", d.process || "—"], ["Observer", d.observer || "—"], ["Date(s)", d.dates || "—"],
    ["Sample size", String(d.sample_size || 0)], ["Unit", d.unit || "min"],
  ], { 0: { fontStyle: "bold", cellWidth: 55 } }) + 6;
  const acts = (d.activities || []).map(a => ({ ...a, _s: tsStats(a.samples || []) }));
  table(ctx, y, ["Activity", "Class", `Mean (${d.unit || "min"})`, "Range", "CV%", "Notes"],
    acts.map(a => [a.name, a.classification, a._s.mean.toFixed(1), a._s.range.toFixed(1), `${a._s.cv.toFixed(0)}%`, a.notes || ""]),
    { 0: { cellWidth: 80 } });
  ctx.finishPage();
  ctx.doc.save(`TimeStudy_${orgName.replace(/\s+/g, "_")}_${new Date().toISOString().slice(0, 10)}.pdf`);
}

// ───────────────────────── Waste Walk ─────────────────────────
const WASTE_LABELS = { T: "Transport", I: "Inventory", M: "Motion", W: "Waiting", O: "Overprocessing", P: "Overproduction", D: "Defects", S: "Skills" };
export async function exportWasteWalkPDF({ project, company, branding }) {
  const d = project?.tool_data || {};
  const orgName = company?.name || "Organisation";
  const ctx = await brandedDoc(branding, "Waste Walk / Gemba Tracker", orgName);
  ctx.newPage(true);
  cover(ctx, "Waste Walk", `${orgName} · ${d.area || "—"}`);
  let y = 50;
  y = table(ctx, y, ["Setup", "Value"], [
    ["Area", d.area || "—"], ["Walked by", d.observer || "—"], ["Date", d.walk_date || "—"], ["Objective", d.objective || "—"],
  ], { 0: { fontStyle: "bold", cellWidth: 55 } }) + 6;
  table(ctx, y, ["Type", "Location", "Description", "Severity", "Cost $", "Suggested Fix"],
    (d.observations || []).map(o => [
      `${o.waste_type} – ${WASTE_LABELS[o.waste_type] || ""}`, o.location || "", o.description || "",
      o.severity || "", String(o.cost_impact || 0), o.suggested_fix || "",
    ]), { 0: { cellWidth: 50 }, 2: { cellWidth: 80 } });
  ctx.finishPage();
  ctx.doc.save(`WasteWalk_${orgName.replace(/\s+/g, "_")}_${new Date().toISOString().slice(0, 10)}.pdf`);
}

// ───────────────────────── RCA (5 Whys + Fishbone) ─────────────────────────
const M_CATS = [["manpower", "Manpower"], ["method", "Method"], ["machine", "Machine"], ["material", "Material"], ["measurement", "Measurement"], ["environment", "Mother Nature"]];
export async function exportRcaPDF({ project, company, branding }) {
  const d = project?.tool_data || {};
  const orgName = company?.name || "Organisation";
  const ctx = await brandedDoc(branding, "5 Whys + Fishbone RCA", orgName);
  ctx.newPage(true);
  cover(ctx, "Root Cause Analysis", `${orgName} · ${d.area || "—"}`);
  let y = 50;
  y = table(ctx, y, ["Element", "Detail"], [
    ["Problem statement", d.problem || "—"], ["Process / area", d.area || "—"],
    ["Owner", d.owner || "—"], ["Detected on", d.detected_at || "—"],
  ], { 0: { fontStyle: "bold", cellWidth: 55 } }) + 6;
  // Fishbone
  const fb = d.fishbone || {};
  const fbRows = M_CATS.map(([k, label]) => [label, ((fb[k] || []).filter(Boolean)).join(" · ") || "—"]);
  ctx.doc.setFontSize(11); ctx.doc.setFont("helvetica", "bold"); ctx.doc.setTextColor(...ctx.primary);
  ctx.doc.text("Fishbone (6M)", 10, y); ctx.doc.setTextColor(...DARK); ctx.doc.setFont("helvetica", "normal"); y += 4;
  y = table(ctx, y, ["Branch", "Potential Causes"], fbRows, { 0: { fontStyle: "bold", cellWidth: 50 } }) + 6;
  // 5 Whys
  if (y + 30 > ctx.H - 12) { ctx.finishPage(); ctx.newPage(); y = 16; }
  ctx.doc.setFontSize(11); ctx.doc.setFont("helvetica", "bold"); ctx.doc.setTextColor(...ctx.primary);
  ctx.doc.text("5 Whys", 10, y); ctx.doc.setTextColor(...DARK); ctx.doc.setFont("helvetica", "normal"); y += 4;
  y = table(ctx, y, ["Cause", "W1", "W2", "W3", "W4", "W5", "Root Cause"],
    (d.whys || []).map(c => [c.cause, ...((c.whys || ["", "", "", "", ""]).slice(0, 5)), c.root_cause || ""])) + 6;
  // Countermeasures
  if (y + 30 > ctx.H - 12) { ctx.finishPage(); ctx.newPage(); y = 16; }
  ctx.doc.setFontSize(11); ctx.doc.setFont("helvetica", "bold"); ctx.doc.setTextColor(...ctx.primary);
  ctx.doc.text("Countermeasures", 10, y); ctx.doc.setTextColor(...DARK); ctx.doc.setFont("helvetica", "normal"); y += 4;
  table(ctx, y, ["Root Cause", "Action", "Owner", "Due"],
    (d.countermeasures || []).map(c => [c.root_cause, c.action, c.owner, c.due]));
  ctx.finishPage();
  ctx.doc.save(`RCA_${orgName.replace(/\s+/g, "_")}_${new Date().toISOString().slice(0, 10)}.pdf`);
}

// ───────────────────────── Pareto ─────────────────────────
export async function exportParetoPDF({ project, company, branding }) {
  const d = project?.tool_data || {};
  const orgName = company?.name || "Organisation";
  const ctx = await brandedDoc(branding, "Pareto Analyzer", orgName);
  ctx.newPage(true);
  cover(ctx, "Pareto Analysis", `${orgName} · ${d.problem_family || "—"}`);
  let y = 50;
  const sorted = [...(d.categories || [])].sort((a, b) => Number(b.frequency || 0) - Number(a.frequency || 0));
  const total = sorted.reduce((a, x) => a + Number(x.frequency || 0), 0);
  let cum = 0;
  const rows = sorted.map((it, i) => {
    const f = Number(it.frequency || 0); cum += f;
    const cumPct = total ? (cum / total) * 100 : 0;
    return [String(i + 1), it.label || "", String(f), `${total ? (f / total * 100).toFixed(1) : 0}%`, `${cumPct.toFixed(1)}%`, cumPct <= 80 ? "Vital Few" : "Trivial"];
  });
  y = table(ctx, y, ["Setup", "Value"], [
    ["Problem family", d.problem_family || "—"], ["Period", d.period || "—"],
    ["Unit", d.unit || "—"], ["Source", d.source || "—"],
    ["Total", String(total)], ["Categories", String(rows.length)],
  ], { 0: { fontStyle: "bold", cellWidth: 55 } }) + 6;
  table(ctx, y, ["#", "Category", "Freq", "Share", "Cum%", "Group"], rows);
  ctx.finishPage();
  ctx.doc.save(`Pareto_${orgName.replace(/\s+/g, "_")}_${new Date().toISOString().slice(0, 10)}.pdf`);
}

// ───────────────────────── Maturity ─────────────────────────
export async function exportMaturityPDF({ project, company, branding }) {
  const d = project?.tool_data || {};
  const orgName = company?.name || "Organisation";
  const ctx = await brandedDoc(branding, "Lean Maturity Assessment", orgName);
  ctx.newPage(true);
  cover(ctx, "Maturity Assessment", `${orgName} · ${d.scope || "—"}`);
  let y = 50;
  const pillars = d.pillars || [];
  const scores = d.scores || {};
  const rows = pillars.map(p => {
    const s = scores[p.id] || { current: 1, target: 3, evidence: "" };
    return [p.label, p.desc || "", String(s.current), String(s.target), String(Math.max(0, s.target - s.current)), s.evidence || ""];
  });
  const overall = rows.length ? rows.reduce((a, r) => a + Number(r[2] || 0), 0) / rows.length : 0;
  y = table(ctx, y, ["Setup", "Value"], [
    ["Scope", d.scope || "—"], ["Assessor", d.assessor || "—"],
    ["Pillars", String(rows.length)], ["Overall (now)", `${overall.toFixed(1)} / 5`],
  ], { 0: { fontStyle: "bold", cellWidth: 55 } }) + 6;
  table(ctx, y, ["Pillar", "Description", "Now", "Target", "Gap", "Evidence"], rows,
    { 0: { fontStyle: "bold", cellWidth: 55 }, 1: { cellWidth: 70 } });
  ctx.finishPage();
  ctx.doc.save(`Maturity_${orgName.replace(/\s+/g, "_")}_${new Date().toISOString().slice(0, 10)}.pdf`);
}

// ───────────────────────── Spaghetti ─────────────────────────
function spDist(p, q) { const dx = q.x - p.x, dy = q.y - p.y; return Math.sqrt(dx * dx + dy * dy); }
function spPathTotal(points, scale) { let d = 0; for (let i = 1; i < (points || []).length; i++) d += spDist(points[i - 1], points[i]); return d * (scale || 1); }
export async function exportSpaghettiPDF({ project, company, branding }) {
  const d = project?.tool_data || {};
  const orgName = company?.name || "Organisation";
  const ctx = await brandedDoc(branding, "Spaghetti / Flow Diagram", orgName);
  ctx.newPage(true);
  cover(ctx, "Spaghetti Diagram", `${orgName} · ${d.area || "—"}`);
  let y = 50;
  const scale = d.scale || 1; const cycles = d.cycles_per_day || 1;
  y = table(ctx, y, ["Setup", "Value"], [
    ["Area", d.area || "—"], ["Scale (m / 100u)", String(scale)],
    ["Cycles / day", String(cycles)], ["Goal", d.goal || "—"],
  ], { 0: { fontStyle: "bold", cellWidth: 55 } }) + 6;
  table(ctx, y, ["Path", "Waypoints", "Per cycle (m)", "Per day (m)"],
    (d.paths || []).map(p => {
      const t = spPathTotal(p.points || [], scale);
      return [p.label || "—", String((p.points || []).length), t.toFixed(1), (t * cycles).toFixed(0)];
    }));
  ctx.finishPage();
  ctx.doc.save(`Spaghetti_${orgName.replace(/\s+/g, "_")}_${new Date().toISOString().slice(0, 10)}.pdf`);
}

// ───────────────────────── PFMEA ─────────────────────────
function rpnVal(s, o, dn) { return Number(s || 0) * Number(o || 0) * Number(dn || 0); }
export async function exportPFMEAPDF({ project, company, branding }) {
  const d = project?.tool_data || {};
  const orgName = company?.name || "Organisation";
  const ctx = await brandedDoc(branding, "PFMEA", orgName);
  ctx.newPage(true);
  cover(ctx, "Process FMEA", `${orgName} · ${d.process || "—"}`);
  let y = 50;
  y = table(ctx, y, ["Setup", "Value"], [
    ["Process", d.process || "—"], ["Team lead", d.team_lead || "—"],
    ["Date", d.review_date || "—"], ["RPN threshold", String(d.rpn_threshold || 100)], ["Team", d.team || "—"],
  ], { 0: { fontStyle: "bold", cellWidth: 55 } }) + 6;
  const rows = [...(d.modes || [])]
    .map(m => ({ ...m, _r: rpnVal(m.severity, m.occurrence, m.detection), _new: m.new_severity != null ? rpnVal(m.new_severity, m.new_occurrence, m.new_detection) : null }))
    .sort((a, b) => b._r - a._r)
    .map(m => [m.step || "", m.failure || "", m.effect || "", String(m.severity), String(m.occurrence), String(m.detection), String(m._r), m.action || "", m.owner || "", m.due || "", m._new != null ? String(m._new) : "—"]);
  table(ctx, y, ["Step", "Failure", "Effect", "S", "O", "D", "RPN", "Action", "Owner", "Due", "New RPN"], rows,
    { 0: { cellWidth: 35 }, 1: { cellWidth: 45 } });
  ctx.finishPage();
  ctx.doc.save(`PFMEA_${orgName.replace(/\s+/g, "_")}_${new Date().toISOString().slice(0, 10)}.pdf`);
}

// ───────────────────────── A3 ─────────────────────────
const A3_BOX_LIST = [
  ["background", "1. Background"], ["current_condition", "2. Current Condition"], ["goal", "3. Goal / Target"], ["root_cause", "4. Root Cause Analysis"],
  ["countermeasures", "5. Countermeasures"], ["implementation", "6. Implementation Plan"], ["verification", "7. Verification / Effect"], ["follow_up", "8. Follow-Up & Standardise"],
];
export async function exportA3PDF({ project, company, branding }) {
  const d = project?.tool_data || {};
  const orgName = company?.name || "Organisation";
  const ctx = await brandedDoc(branding, "A3 Problem Solving", orgName);
  ctx.newPage(true);
  cover(ctx, d.title || "A3", `${orgName} · ${d.owner || "—"} · ${d.status || "draft"}`);
  let y = 50;
  const sec = d.sections || {}, lists = d.lists || {};
  const rows = A3_BOX_LIST.map(([k, label]) => {
    const txt = sec[k] || "";
    const bullets = (lists[k] || []).filter(it => it.text).map(it => `• ${it.text}${it.note ? ` (${it.note})` : ""}`).join("\n");
    return [label, [txt, bullets].filter(Boolean).join("\n")];
  });
  table(ctx, y, ["Section", "Content"], rows, { 0: { fontStyle: "bold", cellWidth: 60 } });
  ctx.finishPage();
  ctx.doc.save(`A3_${orgName.replace(/\s+/g, "_")}_${new Date().toISOString().slice(0, 10)}.pdf`);
}

// ───────────────────────── DMAIC ─────────────────────────
const DMAIC_PHASE_LIST = [["define", "Define"], ["measure", "Measure"], ["analyse", "Analyse"], ["improve", "Improve"], ["control", "Control"]];
export async function exportDmaicPDF({ project, company, branding }) {
  const d = project?.tool_data || {};
  const orgName = company?.name || "Organisation";
  const ctx = await brandedDoc(branding, "DMAIC Charter", orgName);
  ctx.newPage(true);
  cover(ctx, "DMAIC Project Charter", `${orgName} · ${d.project_name || "—"}`);
  let y = 50;
  y = table(ctx, y, ["Charter", "Detail"], [
    ["Project", d.project_name || "—"], ["Sponsor", d.sponsor || "—"], ["Belt lead", d.belt_lead || "—"],
    ["Problem", d.problem || "—"], ["Goal", d.goal || "—"], ["Scope", d.scope || "—"],
    ["Estimated benefit / yr", `$${(d.estimated_benefit || 0).toLocaleString()}`], ["Target completion", d.target_date || "—"],
  ], { 0: { fontStyle: "bold", cellWidth: 55 } }) + 6;
  const phases = d.phases || {};
  const phaseRows = DMAIC_PHASE_LIST.map(([k, label]) => {
    const p = phases[k] || {}; const dels = p.deliverables || [];
    const done = dels.filter(x => x.done).length;
    const dList = dels.map(x => `${x.done ? "✓" : "○"} ${x.item || ""}${x.owner ? ` — ${x.owner}` : ""}${x.due ? ` (${x.due})` : ""}`).join("\n");
    return [label, p.status || "not_started", p.summary || "—", `${done} / ${dels.length}`, dList || "—"];
  });
  table(ctx, y, ["Phase", "Status", "Summary", "Deliverables Done", "Detail"], phaseRows,
    { 0: { fontStyle: "bold", cellWidth: 30 }, 4: { cellWidth: 90 } });
  ctx.finishPage();
  ctx.doc.save(`DMAIC_${orgName.replace(/\s+/g, "_")}_${new Date().toISOString().slice(0, 10)}.pdf`);
}

// ───────────────────────── Yamazumi ─────────────────────────
export async function exportYamazumiPDF({ project, company, branding }) {
  const d = project?.tool_data || {};
  const orgName = company?.name || "Organisation";
  const ctx = await brandedDoc(branding, "Yamazumi / Line Balancing", orgName);
  ctx.newPage(true);
  cover(ctx, "Yamazumi", `${orgName} · ${d.line_name || "—"}`);
  let y = 50;
  const takt = (d.demand_per_period > 0) ? Number(d.available_minutes || 0) / Number(d.demand_per_period) : 0;
  y = table(ctx, y, ["Setup", "Value"], [
    ["Line", d.line_name || "—"], ["Demand / period", String(d.demand_per_period || 0)],
    ["Available minutes", String(d.available_minutes || 0)], ["Period", d.period || "—"],
    ["Takt (min/unit)", takt.toFixed(2)],
  ], { 0: { fontStyle: "bold", cellWidth: 55 } }) + 6;
  const rows = (d.stations || []).map(s => {
    const total = (s.elements || []).reduce((a, e) => a + Number(e.time_min || 0), 0);
    const va = (s.elements || []).filter(e => e.type === "VA").reduce((a, e) => a + Number(e.time_min || 0), 0);
    return [s.name, String((s.elements || []).length), total.toFixed(2), va.toFixed(2), takt > 0 ? `${((total / takt) * 100).toFixed(0)}%` : "—", takt > 0 && total > takt ? "Bottleneck" : (takt > 0 && (total / takt) < 0.7 ? "Under-loaded" : "Balanced")];
  });
  table(ctx, y, ["Station", "Elements", "CT (min)", "VA (min)", "% of Takt", "Status"], rows);
  ctx.finishPage();
  ctx.doc.save(`Yamazumi_${orgName.replace(/\s+/g, "_")}_${new Date().toISOString().slice(0, 10)}.pdf`);
}

// ───────────────────────── Impact × Effort ─────────────────────────
function ieQuad(impact, effort) { const hi = Number(impact) >= 3, he = Number(effort) >= 3; if (hi && !he) return "Quick Wins"; if (hi && he) return "Big Bets"; if (!hi && !he) return "Fill-ins"; return "Time Sinks"; }
export async function exportImpactEffortPDF({ project, company, branding }) {
  const d = project?.tool_data || {};
  const orgName = company?.name || "Organisation";
  const ctx = await brandedDoc(branding, "Impact × Effort Prioritisation", orgName);
  ctx.newPage(true);
  cover(ctx, "Impact × Effort Matrix", `${orgName} · ${d.theme || "—"}`);
  let y = 50;
  y = table(ctx, y, ["Setup", "Value"], [
    ["Theme", d.theme || "—"], ["Facilitator", d.facilitator || "—"],
    ["Session date", d.session_date || "—"], ["Participants", d.participants || "—"],
  ], { 0: { fontStyle: "bold", cellWidth: 55 } }) + 6;
  const rows = (d.ideas || []).map((it, i) => [String(i + 1), it.title || "", String(it.impact), String(it.effort), ieQuad(it.impact, it.effort), it.owner || "", it.note || ""]);
  table(ctx, y, ["#", "Idea", "Impact", "Effort", "Quadrant", "Owner", "Note"], rows, { 1: { cellWidth: 80 } });
  ctx.finishPage();
  ctx.doc.save(`ImpactEffort_${orgName.replace(/\s+/g, "_")}_${new Date().toISOString().slice(0, 10)}.pdf`);
}
